'use client';

import { useEffect } from 'react';
import { reloadOnStaleChunk } from '@/app/project/[uuid]/initiation/board/stale-chunk-reload';

/**
 * СТОРОЖ ПРОТУХШИХ ЧАНКОВ — то, что не ловит ни один React-boundary.
 *
 * `BoardErrorBoundary` перехватывает падение РЕНДЕРА, но самый частый случай
 * приходит мимо него: CSS-чанк грузится тегом `<link>` в `<head>`, и его
 * `error` Next оборачивает в НЕОБРАБОТАННЫЙ реджект промиса — App Router валит
 * страницу целиком, человек видит «Приложение не удалось загрузить». Именно так
 * выглядели issue #28/#30 в GlitchTip, секунда-в-секунду со сменой подов при
 * выкатке (разбор 2026-09-09).
 *
 * Слушаем два события окна:
 *  • `unhandledrejection` — сюда попадает и `ChunkLoadError` из динамического
 *    импорта вне рендера, и обёрнутая ошибка `<link>`;
 *  • `error` в фазе ЗАХВАТА — события ресурсов (`<link>`/`<script>`) не всплывают,
 *    без capture их не увидеть вовсе.
 *
 * Решение о перезагрузке (и защита от цикла) — в `stale-chunk-reload.ts`: одна
 * перезагрузка на вкладку за минуту, маркер в sessionStorage, потому что гард в
 * памяти перезагрузку не переживает (урок rollback-guard).
 *
 * Живёт в layout публичной доски, а не в странице: лендинг `/board` ломался
 * ровно так же, и оба маршрута должны быть прикрыты одинаково.
 */
export function StaleChunkWatch(): null {
  useEffect(() => {
    const onRejection = (e: PromiseRejectionEvent) => {
      // Реджект «ошибкой» бывает и самим Event (target = <link>) — тогда
      // текста нет, и опознаём по элементу-источнику.
      const reason: unknown = e.reason;
      if (reloadOnStaleChunk(reason)) return;
      const target = (reason as { target?: unknown })?.target as HTMLElement | undefined;
      if (isChunkAsset(target)) reloadOnStaleChunk(new Error('Loading chunk asset failed'));
    };
    const onResourceError = (e: Event) => {
      if (isChunkAsset(e.target as HTMLElement | null)) {
        reloadOnStaleChunk(new Error('Loading chunk asset failed'));
      }
    };
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('error', onResourceError, true);
    return () => {
      window.removeEventListener('unhandledrejection', onRejection);
      window.removeEventListener('error', onResourceError, true);
    };
  }, []);
  return null;
}

/** Это упавший ресурс СБОРКИ (чанк Next), а не картинка пользователя? */
function isChunkAsset(el: HTMLElement | null | undefined): boolean {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag !== 'link' && tag !== 'script') return false;
  const url = (el as HTMLLinkElement).href || (el as HTMLScriptElement).src || '';
  // Только собственная статика сборки: чужой CDN и картинки досок трогать нечем,
  // их падение перезагрузкой не лечится.
  return url.includes('/_next/static/');
}
